import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useTranslation } from '../hooks/useTranslation';
import { useCheckout } from '../contexts/CheckoutContext';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import { useDebouncedCallback } from 'use-debounce';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxHeight: '80vh',
  overflow: 'auto',
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[4],
}));

const EmptyCartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const CartIcon = styled(ShoppingCartIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}));

const TotalBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.light,
  borderRadius: theme.spacing(1),
  margin: theme.spacing(2, 0),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5),
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    textAlign: 'inherit',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    textAlign: 'inherit',
  },
  '& .MuiTableBody-root .MuiTableCell-root:last-child': {
    textAlign: 'center',
  },
  '& .MuiTableBody-root .MuiTableCell-root:nth-last-child(-n+3)': {
    textAlign: 'right',
  },
}));

const ProductImage = styled('img')(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: theme.spacing(1),
  objectFit: 'cover',
}));

const QuantityControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

const QuantityText = styled(Typography)(() => ({
  minWidth: '2ch',
  textAlign: 'center',
}));

interface CheckoutProps {
  onClose: () => void;
}

export const Checkout: React.FC<CheckoutProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { items, removeFromCheckout, clearCheckout, updateQuantity } = useCheckout();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});

  // Initialize local quantities when items change
  React.useEffect(() => {
    const quantities = items.reduce((acc, item) => ({
      ...acc,
      [item.item]: item.quantity
    }), {});
    setLocalQuantities(quantities);
  }, [items]);

  const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);

  const debouncedApiCall = useDebouncedCallback(
    async (itemId: string) => {
      try {
        setIsUpdating(prev => ({ ...prev, [itemId]: true }));
        const finalQuantity = localQuantities[itemId];
        await updateQuantity(itemId, finalQuantity);
      } catch (error) {
        console.error('Error updating quantity:', error);
        // Revert local quantity on error
        setLocalQuantities(prev => ({
          ...prev,
          [itemId]: items.find(item => item.item === itemId)?.quantity || 1
        }));
      } finally {
        setIsUpdating(prev => ({ ...prev, [itemId]: false }));
      }
    },
    500 // 500ms delay
  );

  const handleQuantityChange = useCallback((itemId: string, change: number) => {
    // Update local state immediately
    setLocalQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, (prev[itemId] || 1) + change)
    }));
    // Debounce the API call with the final quantity
    debouncedApiCall(itemId);
  }, [debouncedApiCall]);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const handleClearCheckout = () => {
    clearCheckout();
    onClose();
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'Tutorial':
        return t('checkout.productType.tutorial');
      case 'Workshop':
        return t('checkout.productType.workshop');
      case 'Item':
        return t('checkout.productType.item');
      case 'File':
        return t('checkout.productType.file');
      default:
        return type;
    }
  };

  return (
    <StyledPaper>
      <Box sx={{ p: 2, direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {t('checkout.title')}
        </Typography>
        
        {items.length === 0 ? (
          <EmptyCartContainer>
            <CartIcon />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('checkout.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('checkout.emptySubtitle')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
              sx={{ borderRadius: 2 }}
            >
              {t('checkout.continueShopping')}
            </Button>
          </EmptyCartContainer>
        ) : (
          <>
            <TableContainer>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('checkout.type')}</TableCell>
                    <TableCell>{t('checkout.image')}</TableCell>
                    <TableCell>{t('checkout.productTitle')}</TableCell>
                    <TableCell>{t('checkout.price')}</TableCell>
                    <TableCell>{t('checkout.quantity')}</TableCell>
                    <TableCell>{t('checkout.total')}</TableCell>
                    <TableCell>{t('checkout.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.item}>
                      <TableCell>
                        <Chip 
                          label={getProductTypeLabel(item.productType)} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <ProductImage
                          src={item.productDetails?.image || '/placeholder-image.jpg'}
                          alt={item.productDetails?.title || 'Product image'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.productDetails?.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.price} {t('checkout.currency')}
                      </TableCell>
                      <TableCell>
                        {item.productType === 'Item' ? (
                          <QuantityControls>
                            <QuantityButton
                              size="small"
                              onClick={() => handleQuantityChange(item.item, -1)}
                              disabled={localQuantities[item.item] <= 1 || isUpdating[item.item]}
                            >
                              <RemoveIcon fontSize="small" />
                            </QuantityButton>
                            <QuantityText variant="body2">
                              {localQuantities[item.item] || item.quantity}
                            </QuantityText>
                            <QuantityButton
                              size="small"
                              onClick={() => handleQuantityChange(item.item, 1)}
                              disabled={isUpdating[item.item]}
                            >
                              <AddIcon fontSize="small" />
                            </QuantityButton>
                          </QuantityControls>
                        ) : (
                          <Typography variant="body2">
                            {item.quantity}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(item.price * item.quantity).toFixed(2)} {t('checkout.currency')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeFromCheckout(item.item)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
            
            <TotalBox>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" color="primary">
                  {t('checkout.total')}
                </Typography>
                <Typography variant="h6" color="primary">
                  {totalPrice} {t('checkout.currency')}
                </Typography>
              </Stack>
            </TotalBox>

            <Stack direction="row" gap={4} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClearCheckout}
                fullWidth
                sx={{ borderRadius: 2 }}
              >
                {t('checkout.clear')}
              </Button>
              <Button
                variant="contained"
                onClick={handleCheckout}
                fullWidth
                sx={{ borderRadius: 2 }}
              >
                {t('checkout.proceed')}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </StyledPaper>
  );
}; 